// BFSG
import * as React from 'react'
import styled from 'styled-components'
import { Product } from 'modules/listing/types'
import Button from 'theme/atoms/Button'
import calculateDeliveryDate from 'utils/calculateDeliveryDate'
import useCart from 'hooks/account/cart/useCart'
import DeliveryInfo from 'theme/molecules/DeliveryInfo'
import theme from 'theme'
import useTranslations from 'hooks/useTranslations'
import Amount from 'theme/atoms/Amount'
import { navigate } from 'gatsby'
import * as evt from '../events'
import StockInfo from 'theme/molecules/StockInfo'
import useAccount from 'hooks/account/useAccount'
import { isItemChannelRestricted } from 'hooks/account/utils'
import getProductLink from 'utils/getProductLink'

type Props = {
  hit: Product
  listname: string
  listPosition: number
  listid: string
}

export default function BuyBox(props: Props) {
  const [amount, setAmount] = React.useState(1)
  const cart = useCart()
  const [addingProductToCart, setAddingProductToCart] = React.useState(false)
  const t = useTranslations<'molecules-ProductWidgetSeries'>()
  // Amount leitet sein ref an ein Input weiter
  const $amount = React.useRef<HTMLInputElement>(null)

  const isCustomTailor =
    props.hit.configurations?.custom_tailor.is_custom_tailor || false

  const channel = useAccount().channel

  const restricted = React.useMemo(
    () => isItemChannelRestricted(props.hit, channel),
    [channel, props.hit]
  )
  const setValue = (val) => {
    setAmount(val)
  }

  const navigateToPdp = () => {
    const target = getProductLink(props.hit)
    if (target) navigate(target)
  }

  const addToCart = () => {
    setAddingProductToCart(true)
    evt.skuWidgetAddToCart(
      props.hit,
      props.listname,
      props.listPosition,
      props.listid
    )

    const cartItemIndex = cart.data.items.findIndex(
      (item) => item.sw6Uuid === props.hit.sw6Uuid
    )
    if (cartItemIndex === -1) {
      cart.addItem(props.hit.sw6Uuid, amount, {
        item_list_name: props.listname || '',
        list_position: props.listPosition || 0,
        item_list_id: props.listid
      })
    } else {
      cart.updateItem(
        cart.data.items[cartItemIndex].cartItemId,
        amount + cart.data.items[cartItemIndex].quantity
      )
    }
  }

  const deliveryInformations = React.useMemo(
    () => calculateDeliveryDate(props.hit, amount),
    [props.hit, amount]
  )

  React.useEffect(() => {
    if (!addingProductToCart) return
    setAddingProductToCart(cart.updating)
  }, [cart.updating, addingProductToCart])

  return (
    <Wrapper className="BuyBox">
      <div className="wrapper">
        <div className="amount">
          <Amount
            ref={$amount}
            data-cy-handle-amount="amount"
            data-cy-handle-decrease="decrease-amount"
            data-cy-handle-increase="increase-amount"
            minValue={1}
            maxValue={9999}
            setValue={setValue}
            decrease_button_a11y_title={t.asText(
              $amount,
              'molecules_ProductWidgetSeries_buybox_a11y_amount_decrease_button'
            )}
            amount_input_a11y_title={t.asText(
              $amount,
              'molecules_ProductWidgetSeries_buybox_a11y_amount_input'
            )}
            increase_button_a11y_title={t.asText(
              $amount,
              'molecules_ProductWidgetSeries_buybox_a11y_amount_increase_button'
            )}
          />
        </div>
        {/* TODO: refactor to own component; remove conditionals */}
        {props.hit.sellable && deliveryInformations.isBuyable ? (
          restricted ? (
            <Button
              variation="cart_channel_restriction"
              data-cy-state="channel-restricted"
              $fullWidth
              icon={'disabled'}
            >
              {t(channel === 'b2b' ? `only_for_b2c` : `only_for_b2b`)}
            </Button>
          ) : isCustomTailor ? (
            <Button
              variation="cart_disabled"
              data-cy-handle="navigate-to-pdp"
              data-cy-state="is-custom-tailor"
              className="customizable-btn"
              $fullWidth
              onClick={navigateToPdp}
            >
              {t('customizable_btn_label')}
            </Button>
          ) : (
            <Button
              variation={cart.setup ? 'cart' : 'cart_disabled'}
              data-cy-handle="add-to-cart"
              data-cy-state="is-add-to-cart"
              className="cart-btn"
              $fullWidth
              onClick={addToCart}
              disabled={!cart.setup}
              submitable
              icon="cart"
              isSubmitting={addingProductToCart}
              isSubmittingState="adding-to-cart"
            >
              {t('cart_btn_label')}
            </Button>
          )
        ) : (
          <Button
            variation="cart_disabled"
            data-cy-handle="add-to-cart"
            data-cy-state="is-disabled-cart"
            className="sold-out-button"
            $fullWidth
          >
            {t('sold_out_btn_series')}
          </Button>
        )}
      </div>
      <StockInfo stock={props.hit.stock} />
      <DeliveryInfo variant={props.hit} amount={amount} />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;

  > .wrapper {
    display: flex;
    flex-direction: row;
    width: 100%;
    max-width: 500px;
    margin-bottom: ${theme.spacing('xs')};

    > .amount {
      display: flex;
      margin-right: ${theme.spacing('ml')};
    }

    > button {
      height: 2.5rem;
      flex: 1;
    }

    @media (min-width: ${theme.ms.LG}px) {
      flex-direction: row;

      > .amount {
        margin: 0;
        margin-right: ${theme.spacing('ml')};
      }
      > button {
        width: 15rem;
      }
    }

    > .cart-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      > svg {
        margin-right: 8px;
      }
    }
    > .customizable-btn {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    > .sold-out-button {
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: default;
      > svg {
        margin-right: 8px;
      }
    }
  }

  > .DeliveryInfo {
    max-width: 500px;
    width: 100%;
    align-self: flex-end;

    @media (min-width: ${theme.ms.LG}px) {
      max-width: 15rem;
    }

    @media (min-width: ${theme.ms.XL}px) {
      max-width: 500px;
    }
  }
`
